import { IconArrowRight, IconChevronLeft, IconChevronRight, IconHelpCircle, IconPresentation, IconWand } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { userApi } from "./api";
import { useAdminAuth, useUserAuth } from "./auth";
import { Badge, Brand, Button, Icon } from "./ui";
import { ADMIN_BASE_PATH } from "./admin-path";
import { openExistingPptBeautifyDialog } from "./existing-ppt-beautify-launcher";
import {
  PROJECT_COVER_BACKFILL_REFRESH_MS,
  type ProjectItem,
  type ProjectListResponse,
  type UserStorageOverview,
  type WorkspaceOutletContext,
} from "./workspace-projects";

const userNav = [
  { to: "/app", label: "工作台", icon: "home" as const, end: true },
  { to: "/app/projects", label: "我的项目", icon: "files" as const },
  { to: "/app/new", label: "新建 PPT", icon: "plus" as const },
];

const adminNav = [
  { to: ADMIN_BASE_PATH, label: "运营概览", icon: "chart" as const, end: true },
  { to: `${ADMIN_BASE_PATH}/projects`, label: "任务管理", icon: "files" as const },
  { to: `${ADMIN_BASE_PATH}/lab`, label: "PPT 实验室", icon: "flask" as const },
  { to: `${ADMIN_BASE_PATH}/users`, label: "用户与 Tester", icon: "users" as const },
  { to: `${ADMIN_BASE_PATH}/credits`, label: "积分管理", icon: "spark" as const },
  { to: `${ADMIN_BASE_PATH}/themes`, label: "主题管理", icon: "palette" as const },
  { to: `${ADMIN_BASE_PATH}/system`, label: "系统配置", icon: "settings" as const },
];

export function WorkspaceShell() {
  const auth = useUserAuth();
  const location = useLocation();
  const [projects, setProjects] = useState<ProjectItem[]>();
  const [storage, setStorage] = useState<UserStorageOverview>();
  const [projectError, setProjectError] = useState("");
  const nickname = auth.me?.user.nickname || "我的账户";
  useEffect(() => {
    let cancelled = false;
    let coverRefreshTimer: number | undefined;
    setProjectError("");
    const loadProjects = (allowCoverRefresh: boolean) => userApi<ProjectListResponse>("/api/app/v2/ppt-tasks")
      .then((data) => {
        if (cancelled) return;
        setProjects(data.tasks || []);
        setStorage(data.storage);
        if (allowCoverRefresh && data.coverBackfillScheduled) {
          coverRefreshTimer = window.setTimeout(
            () => void loadProjects(false),
            PROJECT_COVER_BACKFILL_REFRESH_MS,
          );
        }
      })
      .catch((caught) => {
        if (cancelled) return;
        setProjectError(caught instanceof Error ? caught.message : "项目加载失败");
        setProjects([]);
      });
    void loadProjects(true);
    return () => {
      cancelled = true;
      if (coverRefreshTimer !== undefined) window.clearTimeout(coverRefreshTimer);
    };
  }, []);
  const outletContext: WorkspaceOutletContext = { projects, storage, projectError };
  const showQuickActions = location.pathname !== "/app/new";
  const hideBeautifyQuickAction = auth.me?.features?.edition === "china"
    && (location.pathname === "/app" || location.pathname === "/app/projects");
  return (
    <div className="application-shell workspace-shell">
      <a className="skip-link" href="#app-main">跳到主要内容</a>
      <aside className="app-sidebar" aria-label="工作台导航">
        <Link className="sidebar-brand sidebar-brand-link" to="/" aria-label="返回幻视PPT官网首页" title="返回官网首页"><Brand /></Link>
        <SidebarDashboardOverview projects={projects} storage={storage} joinedAt={auth.me?.user.registeredAt || auth.me?.user.createdAt}/>
        <nav aria-label="主要功能">{userNav.map((item) => <NavLink key={item.to} to={item.to} end={item.end}><Icon name={item.icon} /><span>{item.label}</span></NavLink>)}<NavLink className="sidebar-mobile-account" to="/app/account" aria-label={`我的账户：${nickname}`} title="账户与额度"><span className="avatar sidebar-mobile-avatar">{nickname.slice(0,1)}</span><span className="sidebar-mobile-account-name">{nickname}</span></NavLink></nav>
        <div className="sidebar-foot workspace-sidebar-foot"><a href="/help"><IconHelpCircle size={19} stroke={1.8}/><span>帮助中心</span></a><NavLink className="workspace-sidebar-account" to="/app/account" aria-label={`我的账户：${nickname}`} title="账户与额度"><span className="avatar">{nickname.slice(0,1)}</span><span className="workspace-sidebar-account-name">{nickname}</span></NavLink></div>
      </aside>
      <div className="app-column">
        {showQuickActions && <header className="app-topbar workspace-app-topbar">
          <WorkspaceQuickActions hideBeautify={hideBeautifyQuickAction}/>
        </header>}
        <main className="app-content" id="app-main" tabIndex={-1}><Outlet context={outletContext}/></main>
      </div>
    </div>
  );
}

function SidebarDashboardOverview({projects,storage,joinedAt}:{projects?:ProjectItem[];storage?:UserStorageOverview;joinedAt?:string}) {
  const [month,setMonth]=useState(()=>{const today=new Date();return new Date(today.getFullYear(),today.getMonth(),1)});
  const year=month.getFullYear();
  const monthIndex=month.getMonth();
  const leading=(new Date(year,monthIndex,1).getDay()+6)%7;
  const daysInMonth=new Date(year,monthIndex+1,0).getDate();
  const today=new Date();
  const records=new Map<number,number>();
  (projects||[]).forEach((project)=>{
    const created=parseProjectDate(project.createdAt);
    if(created&&created.getFullYear()===year&&created.getMonth()===monthIndex)records.set(created.getDate(),(records.get(created.getDate())||0)+1);
  });
  const cells=Array.from({length:42},(_,index)=>{const day=index-leading+1;return day>0&&day<=daysInMonth?day:null});
  const monthlyProjects=(projects||[]).filter((project)=>{const created=parseProjectDate(project.createdAt);return created?.getFullYear()===year&&created.getMonth()===monthIndex});
  const joinedDays=daysSince(joinedAt,today);
  const stats=[
    ["PPT总数",projects?String(projects.length):"–"],
    ["本月创建",projects?String(monthlyProjects.length):"–"],
    ["加入天数",joinedDays == null ? "–" : String(joinedDays)],
  ];
  return <section className="dashboard-overview sidebar-dashboard-overview" aria-labelledby="sidebar-creation-calendar-title">
    <div className="creation-calendar">
      <header><div><span>创作日历</span><h2 id="sidebar-creation-calendar-title">{year}年 {monthIndex+1}月</h2></div><div className="calendar-controls"><button type="button" onClick={()=>setMonth(new Date(year,monthIndex-1,1))} aria-label="查看上个月"><IconChevronLeft size={16}/></button><button type="button" onClick={()=>setMonth(new Date(year,monthIndex+1,1))} aria-label="查看下个月"><IconChevronRight size={16}/></button></div></header>
      <div className="calendar-weekdays" aria-hidden="true">{["一","二","三","四","五","六","日"].map((day)=><span key={day}>{day}</span>)}</div>
      <div className="calendar-grid">{cells.map((day,index)=>day?<div key={`${year}-${monthIndex}-${day}`} className={`${records.has(day)?"has-record ":""}${today.getFullYear()===year&&today.getMonth()===monthIndex&&today.getDate()===day?"is-today":""}`} aria-label={`${year}年${monthIndex+1}月${day}日${records.has(day)?`，创建了 ${records.get(day)} 个 PPT`:""}`}><span>{day}</span>{records.has(day)&&<IconPresentation size={9} stroke={2.2}/>}</div>:<div className="calendar-empty" key={`empty-${index}`} aria-hidden="true"/>)}</div>
    </div>
    <div className="dashboard-statistics"><header><span>数据概览</span><small>随月份联动</small></header><div>{stats.map(([label,value])=><article key={label}><strong>{value}</strong><span>{label}</span></article>)}</div></div>
    <StorageOverview storage={storage}/>
  </section>;
}

function StorageOverview({storage}:{storage?:UserStorageOverview}) {
  if(!storage)return <div className="sidebar-storage-overview"><header><span>存储空间</span><small>读取中…</small></header></div>;
  const percent=storage.quotaBytes>0?Math.min(100,Math.round(storage.usedBytes/storage.quotaBytes*100)):0;
  const message=storage.blocked?"空间已超限，请删除内容后继续":storage.overQuota?"已超过建议额度，当前仍可继续使用":storage.measuredAt?`最近统计 ${new Date(storage.measuredAt).toLocaleString("zh-CN")}`:"首次统计中";
  return <div className={`sidebar-storage-overview ${storage.overQuota?"is-over":""}`}>
    <header><span>存储空间</span><small>{formatStorageBytes(storage.usedBytes)} / {formatStorageBytes(storage.quotaBytes)}</small></header>
    <div className="sidebar-storage-progress" role="progressbar" aria-label="存储空间使用率" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}><i style={{width:`${percent}%`}}/></div>
    <p>{message}</p>
  </div>;
}

function formatStorageBytes(bytes:number):string {
  if(bytes<1024*1024)return `${Math.max(0,bytes/1024).toFixed(bytes>0?1:0)} KiB`;
  if(bytes<1024*1024*1024)return `${(bytes/1024/1024).toFixed(1)} MiB`;
  return `${(bytes/1024/1024/1024).toFixed(2)} GiB`;
}

function parseProjectDate(value?:string):Date|undefined {
  if(!value)return undefined;
  const date=new Date(value);
  return Number.isNaN(date.getTime())?undefined:date;
}

function daysSince(value:string|undefined,today:Date):number|undefined {
  const joined=parseProjectDate(value);
  if(!joined)return undefined;
  const joinedDay=new Date(joined.getFullYear(),joined.getMonth(),joined.getDate()).getTime();
  const todayDay=new Date(today.getFullYear(),today.getMonth(),today.getDate()).getTime();
  return Math.max(1,Math.floor((todayDay-joinedDay)/86_400_000)+1);
}

function WorkspaceQuickActions({hideBeautify=false}:{hideBeautify?:boolean}) {
  return <section className={`workspace-quick-actions ${hideBeautify?"single-option":""}`} aria-label="PPT 快捷操作">
    <Link className="workspace-quick-action action-create" to="/app/new">
      <span className="workspace-quick-action-copy"><strong>生成PPT</strong><p>对话式生成文档，一句话生成 PPT，也可以导入多种资料创建演示文稿。</p><span>开始生成 <IconArrowRight size={16} stroke={2}/></span></span>
      <span className="workspace-quick-action-icon" aria-hidden="true"><IconPresentation size={66} stroke={1.35}/></span>
    </Link>
    {!hideBeautify&&<button className="workspace-quick-action action-beautify" type="button" onClick={openExistingPptBeautifyDialog}>
      <span className="workspace-quick-action-copy"><strong>美化现有PPT</strong><p>上传现有 PPTX，AI 识别并重构视觉设计，保留内容并提升表现力。</p><span>上传美化 <IconArrowRight size={16} stroke={2}/></span></span>
      <span className="workspace-quick-action-icon" aria-hidden="true"><IconWand size={66} stroke={1.35}/></span>
    </button>}
  </section>;
}

export function AdminShell() {
  const auth = useAdminAuth();
  const userAuth = useUserAuth();
  const navigate = useNavigate();
  const tester = auth.auth?.session.role === "tester";
  const visibleNav = tester
    ? adminNav.filter((item) => item.to === `${ADMIN_BASE_PATH}/lab` || item.to === `${ADMIN_BASE_PATH}/themes`)
        .map((item) => item.to === `${ADMIN_BASE_PATH}/themes` ? { ...item, label: "主题测试" } : item)
    : adminNav;
  return (
    <div className="application-shell admin-shell">
      <aside className="app-sidebar admin-sidebar">
        <div className="sidebar-brand"><Brand /><Badge tone={tester ? "warning" : "brand"}>{tester ? "Tester" : "Admin"}</Badge></div>
        <nav>{visibleNav.map((item) => <NavLink key={item.to} to={item.to} end={item.end}><Icon name={item.icon} /><span>{item.label}</span></NavLink>)}</nav>
        <div className="sidebar-foot"><a href="/app">用户工作台</a><a href="/">幻视PPT 官网</a></div>
      </aside>
      <div className="app-column">
        <header className="app-topbar admin-topbar"><div className="mobile-brand"><Brand compact /></div><div className="topbar-context"><span className="topbar-title">{tester ? "PPT 测试空间" : "管理后台"}</span><Badge>{auth.auth?.session.role}</Badge></div><div className="topbar-account"><span className="account-name">{tester ? userAuth.me?.user.nickname : auth.auth?.session.username}</span><Button variant="ghost" onClick={() => void (tester ? userAuth.logout().then(() => navigate("/app/login")) : auth.logout().then(() => navigate(`${ADMIN_BASE_PATH}/login`)))}><Icon name="logout" />退出</Button></div></header>
        <main className="app-content"><Outlet /></main>
      </div>
    </div>
  );
}
